import { DateTime } from 'luxon'
import { formatGoalAmount } from '../../goalTypes'

/** Canned alternatives for goal setup; callers may pass their own list for row-level fields. */
export const NO_TIMEFRAME_LABEL = 'No timeframe set'
export const NO_CAPACITY_LABEL = 'No capacity set'

/**
 * The labels and dates are one table so inserting or reordering a row cannot silently change what an
 * existing label means. "This year" deliberately stays between six and twelve months in the requested
 * ladder even though its exact distance depends on when the goal is created.
 */
const TIMEFRAME_LADDER: { label: string; endDate: (today: DateTime) => DateTime }[] = [
    { label: 'Next 7 days', endDate: (today) => today.plus({ days: 7 }) },
    { label: 'Next 2 weeks', endDate: (today) => today.plus({ weeks: 2 }) },
    { label: 'Next 30 days', endDate: (today) => today.plus({ days: 30 }) },
    { label: 'Next 60 days', endDate: (today) => today.plus({ days: 60 }) },
    { label: 'Next 90 days', endDate: (today) => today.plus({ days: 90 }) },
    { label: 'Next 6 months', endDate: (today) => today.plus({ months: 6 }) },
    { label: 'This year', endDate: (today) => today.endOf('year') },
    { label: 'Next 12 months', endDate: (today) => today.plus({ months: 12 }) },
]

export const TIMEFRAME_OPTIONS = [NO_TIMEFRAME_LABEL, ...TIMEFRAME_LADDER.map(({ label }) => label)]

const CUSTOM_TIMEFRAME_FORMAT = "'By' MMM d, yyyy"

const customEndDateForTimeframe = (timeframeLabel: string): string | undefined => {
    const parsed = DateTime.fromFormat(timeframeLabel, CUSTOM_TIMEFRAME_FORMAT, { locale: 'en-US' })
    return parsed.isValid ? parsed.toISODate() : undefined
}

/** The finished label emitted by Custom…; it is also the only custom shape parsed back into a date. */
export const timeframeLabelForDate = (date: string): string | undefined => {
    const parsed = DateTime.fromISO(date)
    return parsed.isValid ? parsed.setLocale('en-US').toFormat(CUSTOM_TIMEFRAME_FORMAT) : undefined
}

/** A native date-input value, accepted only when it is today or later. */
export const customTimeframeDate = (date: string, today = DateTime.now()): string | undefined => {
    const parsed = DateTime.fromISO(date)
    if (!parsed.isValid || parsed.startOf('day') < today.startOf('day')) return undefined
    return parsed.toISODate()
}

/**
 * A specific "By …" label has to move with a later end-date edit or it becomes a false statement.
 * Other labels stay verbatim: canned spans and free-typed context are descriptions, not date mirrors.
 */
export const timeframeLabelAfterEndDateEdit = (
    timeframeLabel: string,
    normalizedEndDate: string
): string | undefined => {
    if (!customEndDateForTimeframe(timeframeLabel)) return undefined
    return normalizedEndDate ? timeframeLabelForDate(normalizedEndDate) : NO_TIMEFRAME_LABEL
}

/** The machine-readable end date behind every canned or Custom… timeframe label. */
export const endDateForTimeframe = (timeframeLabel: string, today = DateTime.now()): string | undefined => {
    const canned = TIMEFRAME_LADDER.find(({ label }) => label === timeframeLabel)
    if (canned) return canned.endDate(today).toISODate()

    return customEndDateForTimeframe(timeframeLabel)
}

/**
 * Capacity is picked in one of two units: hours a week, or sessions a week. Both are written as a
 * label, and this module is the only place that knows how — `capacityLabelFor` writes one and
 * `capacityFromLabel` reads it back. Keeping the pair here is what lets the pill, the saved goal's
 * weeklyTarget/unit, and redraft round-trip a value without three near-copies of the same shape.
 *
 * It is the same arrangement `endDateForTimeframe` already uses above: the label stays the thing the
 * user picked, and the number behind it is derived from that label on demand rather than stored
 * twice and kept in sync by hand.
 */
export type TCapacityUnit = 'hrs' | 'sessions'

export interface TStatedCapacity {
    value: number
    unit: TCapacityUnit
}

export const capacityLabelFor = (value: number, unit: TCapacityUnit): string =>
    unit === 'hrs' ? `~${formatGoalAmount(value)} hrs / week` : `${formatGoalAmount(value)}× / week`

/** Mirrored in Go by parseCapacityLabel (backend/api/goal_draft.go) so the planner reads the same ceiling. */
const CAPACITY_LABEL = /^~?\s*(\d+(?:\.\d+)?)\s*(hrs?|hours?|x|×)\s*\/\s*week$/i

export const capacityFromLabel = (label: string): TStatedCapacity | undefined => {
    const match = label.trim().match(CAPACITY_LABEL)
    if (!match) return undefined
    const value = Number(match[1])
    if (!Number.isFinite(value) || value <= 0) return undefined
    const isSessions = match[2] === 'x' || match[2] === 'X' || match[2] === '×'
    return { value, unit: isSessions ? 'sessions' : 'hrs' }
}

/**
 * Ascending ladders, low end first — the floor is what sent John here: a one-hour-a-week goal was
 * unpickable. Anything off the ladder (2.5 hrs, 20 hrs) goes through Custom…, so these stay short
 * enough to scan rather than trying to cover every number.
 */
const CAPACITY_HOUR_STEPS = [1, 2, 3, 5, 8, 12]
const CAPACITY_SESSION_STEPS = [1, 2, 3, 4, 5, 7]

/**
 * One tab of a PropertyPill that offers more than one unit. `custom` is what turns the tab's numeric
 * entry on: it bounds what can be typed and formats the result into the same shape as `options`, so
 * a typed value and a picked one are indistinguishable downstream.
 */
export interface TPropertyUnitCustom {
    min: number
    max: number
    step: number
    format: (value: number) => string
}

export interface TPropertyUnit {
    key: TCapacityUnit
    label: string
    options: string[]
    custom?: TPropertyUnitCustom
}

/**
 * The typed value, or undefined when it is not a pick at all. `step` is enforced, not just hinted to
 * the number input: a 2.5th session does not exist, and the ceiling the planner is held to is a whole
 * number of sessions — so accepting 2.5 here would mean the server floored the pick to 2 behind the
 * user's back rather than the picker declining it to their face.
 */
export const customCapacityValue = (raw: string, range: TPropertyUnitCustom): number | undefined => {
    if (raw.trim() === '') return undefined
    const value = Number(raw)
    if (!Number.isFinite(value) || value < range.min || value > range.max) return undefined
    const steps = value / range.step
    // Tolerance, not equality: 2.5 / 0.5 is exact but plenty of decimal/step pairs are not.
    if (Math.abs(steps - Math.round(steps)) > 1e-9) return undefined
    return value
}

/** Hours take halves; a half session is not a thing, matching the weekly-target chip in GoalDetails. */
export const CAPACITY_UNITS: TPropertyUnit[] = [
    {
        key: 'hrs',
        label: 'Hours',
        options: CAPACITY_HOUR_STEPS.map((hours) => capacityLabelFor(hours, 'hrs')),
        custom: { min: 0.5, max: 60, step: 0.5, format: (value: number) => capacityLabelFor(value, 'hrs') },
    },
    {
        key: 'sessions',
        label: 'Sessions',
        options: CAPACITY_SESSION_STEPS.map((sessions) => capacityLabelFor(sessions, 'sessions')),
        custom: { min: 1, max: 30, step: 1, format: (value: number) => capacityLabelFor(value, 'sessions') },
    },
]

/** What the capacity pill lists above its unit tabs — the rungs themselves live in CAPACITY_UNITS. */
export const CAPACITY_OPTIONS = [NO_CAPACITY_LABEL]
